/**
 * The signed-in strip: where you can go, who you are, and the way out.
 *
 * Until now the app had no sign-out at all — `supabase.auth.signOut()` was never
 * called anywhere in src/ — and no page told you which account you were using.
 * With `persistSession: true` that meant one person stayed signed in forever on
 * the kitchen iPad and the next person silently edited their household.
 *
 * It also carries the app's only navigation. Screens used to link ad-hoc to
 * whichever screen their author had in mind, so /trips and /trip-setup were
 * reachable from nowhere at all and the rest depended on where you happened to
 * start. One menu, on every screen, is the fix.
 *
 * Mounted once at the root (src/main.tsx), above the router, so every screen gets
 * it without any page opting in. Renders NOTHING when signed out, which keeps the
 * public landing page, /login and /join exactly as they were.
 *
 * Deliberately not sticky: /intake has its own sticky progress header at top 0,
 * and two stacked sticky bars on a phone eat the screen.
 *
 * It deliberately does NOT name the trip. The bar sits above the router and cannot
 * know which trip the page below it is showing — /regions?trip=<id> can be a
 * different trip from the one a "current trip" lookup would pick — and a header
 * confidently naming the wrong trip is worse than one that names none.
 */
import { useEffect, useState, type JSX } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/auth'

/** Screens that own their whole canvas and would be spoiled by app chrome. */
const BARE_ROUTES = ['/extrafun']

const LINKS: { href: string; label: string; hint: string }[] = [
  { href: '/intake', label: 'Your household', hint: 'Your answers' },
  { href: '/windows', label: 'Dates', hint: 'When everyone can go' },
  { href: '/regions', label: 'Places', hint: 'Where, ranked by travel' },
  { href: '/trips', label: 'Trip setup', hint: 'Households and invitations' },
  { href: '/extrafun', label: 'Extra fun', hint: 'Games' },
]

const CSS = `
.spz-sessionbar {
  position: relative;
  display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
  padding: 0.5rem max(1rem, env(safe-area-inset-left)) 0.5rem max(1rem, env(safe-area-inset-right));
  border-bottom: 1px solid var(--line);
  font-size: 0.8rem;
}
.spz-sessionbar a.spz-sb-home {
  font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
  text-decoration: none; color: var(--accent); flex: none;
}
.spz-sb-right { display: flex; align-items: center; gap: 0.5rem; min-width: 0; }
/* The email is the first thing to give up space on a narrow phone. */
.spz-sb-who {
  color: var(--muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.spz-sb-out, .spz-sb-toggle {
  flex: none; font: inherit; font-size: 0.8rem; font-weight: 600;
  min-height: 44px; padding: 0.3rem 0.8rem;
  border: 1px solid var(--line); border-radius: 999px;
  background: transparent; color: var(--ink);
  display: inline-flex; align-items: center; gap: 0.4rem;
}
.spz-sb-out:disabled { opacity: 0.5; }
.spz-sb-out:focus-visible, .spz-sb-toggle:focus-visible, .spz-sb-menu a:focus-visible {
  outline: 2px solid var(--accent); outline-offset: 2px;
}
.spz-sb-toggle[aria-expanded='true'] { background: var(--accent); color: var(--ground); border-color: var(--accent); }
.spz-sb-caret { font-size: 0.6rem; line-height: 1; }

/* Tap anywhere else to close — the behaviour a phone expects. */
.spz-sb-scrim { position: fixed; inset: 0; z-index: 40; background: transparent; border: 0; padding: 0; }

.spz-sb-menu {
  position: absolute; z-index: 41; top: 100%; right: 0;
  width: min(20rem, calc(100vw - 1rem));
  margin: 0.35rem 0.5rem 0 0;
  background: var(--ground);
  border: 1px solid var(--line); border-radius: 0.75rem;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28);
  padding: 0.35rem; display: flex; flex-direction: column;
}
.spz-sb-menu a {
  display: flex; flex-direction: column; gap: 0.1rem;
  min-height: 48px; justify-content: center;
  padding: 0.5rem 0.7rem; border-radius: 0.5rem;
  text-decoration: none; color: inherit; font-size: 0.95rem; font-weight: 600;
}
.spz-sb-menu a .spz-sb-hint { font-size: 0.75rem; font-weight: 400; color: var(--muted); }
.spz-sb-menu a[aria-current='page'] { background: color-mix(in srgb, currentColor 8%, transparent); }
.spz-sb-menu hr { border: 0; border-top: 1px solid var(--line); margin: 0.35rem 0.2rem; }
.spz-sb-menu .spz-sb-email {
  font-size: 0.75rem; color: var(--muted); padding: 0 0.7rem 0.4rem;
  overflow-wrap: anywhere;
}
@media (max-width: 26rem) {
  .spz-sb-who { display: none; }
}
`

/** Current pathname, kept in step with the glob router's popstate navigation. */
function usePathname(): string {
  const [path, setPath] = useState(() => window.location.pathname)
  useEffect(() => {
    const onPop = (): void => setPath(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  return path
}

export function SessionBar(): JSX.Element | null {
  const { session, user } = useAuth()
  const path = usePathname()
  const [signingOut, setSigningOut] = useState(false)
  const [open, setOpen] = useState(false)

  const here = path.replace(/\/+$/, '') || '/'

  useEffect(() => setOpen(false), [path])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // Signed out: render nothing at all. The landing page is public and should not
  // grow a chrome bar just because this mounted above it.
  if (!session) return null
  if (BARE_ROUTES.includes(here)) return null

  async function signOut(): Promise<void> {
    setSigningOut(true)
    await supabase.auth.signOut()
    // Full reload rather than a client-side navigate: every screen holds its own
    // membership and household state, and a hard load is the only way to be sure
    // none of the previous person's data is still on screen.
    window.location.assign('/')
  }

  return (
    <div className="spz-sessionbar">
      <style href="spz-sessionbar" precedence="spz">
        {CSS}
      </style>
      <a className="spz-sb-home" href="/">
        SPZ
      </a>
      <div className="spz-sb-right">
        <span className="spz-sb-who" title={user?.email ?? undefined}>
          {user?.email ?? 'Signed in'}
        </span>
        <button
          type="button"
          className="spz-sb-toggle"
          aria-expanded={open}
          aria-controls="spz-sb-menu"
          onClick={() => setOpen((v) => !v)}
        >
          Menu <span className="spz-sb-caret" aria-hidden="true">{open ? '▲' : '▼'}</span>
        </button>
      </div>

      {open && (
        <>
          <button
            type="button"
            className="spz-sb-scrim"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          />
          <nav className="spz-sb-menu" id="spz-sb-menu" aria-label="Screens">
            {LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                aria-current={here === link.href ? 'page' : undefined}
              >
                {link.label}
                <span className="spz-sb-hint">{link.hint}</span>
              </a>
            ))}
            <hr />
            <p className="spz-sb-email">Signed in as {user?.email ?? 'this device'}</p>
            <button
              type="button"
              className="spz-sb-out"
              onClick={() => void signOut()}
              disabled={signingOut}
            >
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </nav>
        </>
      )}
    </div>
  )
}

export default SessionBar
